/********************************************************************************
 * Copyright (c) 2026 CrossBreeze, EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

import {
   type Action,
   CenterCommand,
   CheckEdgeResultAction,
   ComputedBoundsAction,
   FitToScreenCommand,
   FocusStateChangedAction,
   GLSPActionDispatcher,
   GLSPModelSource,
   LocalComputedBoundsAction,
   MessageAction,
   MoveCommand,
   Operation,
   RedoAction,
   RejectAction,
   RequestAction,
   RequestBoundsAction,
   RequestCheckEdgeAction,
   RequestContextActions,
   RequestModelAction,
   RequestPopupModelAction,
   RequestTypeHintsAction,
   ResponseAction,
   ServerAction,
   SetDirtyStateAction,
   SetEditModeAction,
   SetMarkersAction,
   SetModelAction,
   SetPopupModelAction,
   SetTypeHintsAction,
   SetViewportCommand,
   StatusAction,
   UndoAction,
   UpdateModelAction
} from '@eclipse-glsp/client';
import {
   type Clock,
   Deferred,
   type Logger,
   PendingWorkTracker,
   RequestEditsSettledAction,
   reviveProtocolErrorJson,
   type Stopwatch,
   SystemClock,
   TIMED_OUT,
   type Tracer
} from '@hydranium/protocol';
import { inject, injectable, optional, unmanaged } from 'inversify';
import { HydraniumClientTypes } from './types';

/** Default subset of action kinds the framework dispatcher will log: the GLSP
 *  traffic an observer reading the log typically wants to see. All
 *  are standard `@eclipse-glsp/client` kinds; adopters with custom action kinds
 *  extend via `extraLoggedKinds`. */
export const HYDRANIUM_DEFAULT_LOGGED_KINDS: ReadonlySet<string> = new Set<string>([
   SetModelAction.KIND,
   UpdateModelAction.KIND,
   RequestBoundsAction.KIND,
   ComputedBoundsAction.KIND,
   SetDirtyStateAction.KIND,
   SetEditModeAction.KIND,
   SetMarkersAction.KIND,
   SetTypeHintsAction.KIND,
   StatusAction.KIND,
   MessageAction.KIND,
   RejectAction.KIND,
   RequestContextActions.KIND,
   RequestModelAction.KIND,
   RequestTypeHintsAction.KIND,
   RequestCheckEdgeAction.KIND,
   CheckEdgeResultAction.KIND
]);

/** Default request → response kind pairs for GLSP 'flow' actions that do NOT
 *  carry a requestId/responseId — so the dispatcher matches by kind (FIFO).
 *  Id-based matching still wins when a requestId is present. */
export const HYDRANIUM_DEFAULT_KIND_PAIRS: ReadonlyMap<string, string> = new Map<string, string>([
   [RequestModelAction.KIND, SetModelAction.KIND],
   [RequestTypeHintsAction.KIND, SetTypeHintsAction.KIND],
   [RequestBoundsAction.KIND, ComputedBoundsAction.KIND]
]);

/** Additive options. Adopters with custom action kinds pass extras here; the
 *  framework defaults always apply (you cannot un-log a kind the framework
 *  considers worth logging — subclass + override `dispatch` if you must). */
export interface HydraniumGlspActionDispatcherOptions {
   readonly extraLoggedKinds?: Iterable<string>;
   readonly extraKindPairs?: Iterable<readonly [string, string]>;
   /** Replaces the default `summarize` body entirely. Use for adopter-domain
    *  action kinds where the framework default returns ''. */
   readonly summarize?: (action: Action) => string;
}

/**
 * Wraps every dispatch so action traffic appears in the host's log, the Theia
 * Output channel or a page's console, interleaved with server log lines where
 * the host shows both. Instrumenting the dispatcher
 * (rather than per-kind handlers) catches ResponseActions that bypass the
 * handler pipeline.
 *
 * The framework defaults cover the standard GLSP kinds worth logging, not every
 * kind the protocol defines. Adopters with custom kinds either pass
 * `extraLoggedKinds` / `extraKindPairs` to the constructor (via a
 * `toDynamicValue` binding) or subclass and override.
 *
 * Where the host asks for it, it also counts the edits it sends that the server
 * has not answered yet, as {@link editsInFlight}, so a page can ask before a
 * reload drops one. The dirty state cannot say this: the server sets it only
 * once it has answered, and an edit that changed nothing is answered with
 * nothing at all. Each edit is followed by a {@link RequestEditsSettledAction},
 * which the server answers after it, and counts until that request or a later
 * one is answered or rejected: the server handles them in order. With no
 * answer, as when the connection is lost, the count stays, so a guard asks
 * rather than loses the edit. An action from the server is not counted, nor
 * is anything on a server that serves no settle request.
 */
@injectable()
export class HydraniumGlspActionDispatcher extends GLSPActionDispatcher {
   @inject(HydraniumClientTypes.Logger) protected readonly hydraniumLogger!: Logger;
   @inject(HydraniumClientTypes.Tracer) protected readonly tracer!: Tracer;
   /** Times each request to its response: the diagram container's {@link Clock}, or a `SystemClock`. */
   @inject(HydraniumClientTypes.Clock) @optional() protected readonly clock: Clock = new SystemClock();
   /** Whether to count edits in flight; the host asks for it through `createGlspClientModule`. */
   @inject(HydraniumClientTypes.TrackEditsInFlight) @optional() protected readonly trackEditsInFlight: boolean = false;

   protected readonly loggedKinds: ReadonlySet<string>;
   protected readonly kindPairs: ReadonlyMap<string, string>;
   protected readonly kindPairsReverse: ReadonlyMap<string, string>;

   /** Per edit sent, until its {@link RequestEditsSettledAction} or a later one is answered or rejected. */
   protected readonly pendingEdits = new PendingWorkTracker();
   /** The edits in flight by the number of their settle request, in send order. */
   protected readonly editsBySettle = new Map<number, Deferred>();
   protected settlesSent = 0;
   /**
    * How long {@link whenEditsSettled} waits before going on anyway: well past
    * the server's bounded waits on one settle together. Its handling of the
    * edits before has no bound, so a server slower than that is taken as lost.
    */
   protected readonly settleEditsMs: number = 30_000;
   /** The postponed actions a lifted block is dispatching a second time, counted when first sent. */
   protected redispatched: ReadonlySet<Action> | undefined;
   /** The popup request whose answer may still show, until {@link trackPopupRequest} ends it. */
   protected popupRequestId: string | undefined;
   /** Starts the id given to a popup request, so only answers to those can be dropped as stale. */
   protected readonly popupRequestPrefix: string = 'popup_';
   /** What closes a shown popup in GLSP's hover module, so it ends a pending popup request too. */
   protected readonly popupClosingKinds: ReadonlySet<string> = new Set([
      FitToScreenCommand.KIND,
      CenterCommand.KIND,
      SetViewportCommand.KIND,
      MoveCommand.KIND,
      FocusStateChangedAction.KIND
   ]);

   protected pairCounter = 0;
   /** Pending requests keyed by requestId (for actions that carry one). */
   protected pendingById = new Map<string, { id: number; sinceRequest: Stopwatch }>();
   /** FIFO queue per response kind, used for `flow` pairs without requestId/responseId. */
   protected pendingByKind = new Map<string, Array<{ id: number; sinceRequest: Stopwatch }>>();

   constructor(@unmanaged() options?: HydraniumGlspActionDispatcherOptions) {
      super();
      const extraLogged = options?.extraLoggedKinds ? Array.from(options.extraLoggedKinds) : [];
      this.loggedKinds = new Set<string>([...HYDRANIUM_DEFAULT_LOGGED_KINDS, ...extraLogged]);
      const extraPairs = options?.extraKindPairs ? Array.from(options.extraKindPairs) : [];
      const pairs = new Map<string, string>([...HYDRANIUM_DEFAULT_KIND_PAIRS, ...extraPairs]);
      this.kindPairs = pairs;
      this.kindPairsReverse = new Map<string, string>(Array.from(pairs, ([req, res]) => [res, req]));
      if (options?.summarize) {
         this.summarize = options.summarize;
      }
   }

   override initialize(): Promise<void> {
      // `initialize()` is called many times; super is idempotent, but our callback registration
      // isn't. Register the timing log only on the first call (when super.initialized is unset).
      if (!this.initialized) {
         this.registerInitTiming();
      }
      return super.initialize();
   }

   /**
    * Register the one-shot 'model initialized' timing line. Instrumentation
    * only — guarded so a tracer failure degrades logging rather than aborting
    * diagram init. `initialize()` runs inside `DiagramLoader.load()`, so an
    * error thrown here fails the whole load over a log line.
    *
    * GLSP 2.7: the load's caller (`GLSPDiagramWidget.onAfterAttach`) does not
    * catch it either, so the error would escape as an uncaught rejection.
    */
   protected registerInitTiming(): void {
      try {
         this.tracer.time('Model initialized', () => this.onceModelInitialized(), 'info', { logAfterMs: 0 });
      } catch (err) {
         this.hydraniumLogger.error('Failed to register model-initialized timing instrumentation', err);
      }
   }

   /** How many edits the server has not answered yet; see {@link isEdit}. */
   get editsInFlight(): number {
      return this.pendingEdits.size;
   }

   /**
    * Resolves to `true` once the edits in flight now have been answered or
    * rejected, or to `false` once {@link settleEditsMs} has passed first. An
    * edit sent while it waits is not waited for. Never rejects. For a save or a
    * switch that must not run ahead of an edit the server has not answered.
    */
   async whenEditsSettled(): Promise<boolean> {
      if (this.pendingEdits.isIdle) {
         return true;
      }
      return (await this.clock.raceTimer(this.pendingEdits.whenSettled(), this.settleEditsMs)) !== TIMED_OUT;
   }

   override dispatch(action: Action): Promise<void> {
      this.trackPopupRequest(action);
      if (this.loggedKinds.has(action.kind)) {
         this.logAction(action);
      }
      const dispatched = super.dispatch(action);
      this.trackEdit(action);
      return dispatched;
   }

   /**
    * As GLSP's, except that a reject whose `detail` is a JSON-RPC error
    * envelope, as the server sends for a typed error such as `SaveFailedError`,
    * rejects the request with that error revived. GLSP's rejects with the
    * reject's message alone, so a caller could not read what failed. The
    * accompanying test pins GLSP's body, whose timeout and request handling
    * this repeats.
    *
    * It also drops an answer {@link isStalePopupAnswer} calls stale. The check
    * sits here because GLSP clears the `responseId` of an answer nobody awaits
    * before any hover class sees it.
    */
   protected override async handleResponseAction(action: ResponseAction): Promise<void> {
      const request = this.requests.get(action.responseId);
      if (!request && this.isStalePopupAnswer(action)) {
         this.logger.log(this, 'Dropping a popup answer to a request that was replaced or closed', action);
         return;
      }
      if (!request || !RejectAction.is(action) || action.detail === undefined) {
         return super.handleResponseAction(action);
      }
      const error = reviveProtocolErrorJson(action.detail);
      if (!error) {
         return super.handleResponseAction(action);
      }
      const timeout = this.timeouts.get(action.responseId);
      if (timeout !== undefined) {
         clearTimeout(timeout);
         this.timeouts.delete(action.responseId);
      }
      this.requests.delete(action.responseId);
      request.reject(error);
      this.logger.warn(this, `Request with id ${action.responseId} failed.`, action.message, action.detail);
   }

   protected override handleBlocked(action: Action, predicate: (action: Action) => boolean): Promise<void> {
      if (!predicate(action)) {
         return super.handleBlocked(action, predicate);
      }
      // A lifted block dispatches what it postponed again, within this call.
      this.redispatched = new Set(this.postponedActions.map(postponed => postponed.action));
      try {
         return super.handleBlocked(action, predicate);
      } finally {
         this.redispatched = undefined;
      }
   }

   /** Whether `action` changes the model on the server: an operation, an undo or a redo. Widen it for an edit kind of your own. */
   protected isEdit(action: Action): boolean {
      return Operation.is(action) || UndoAction.is(action) || RedoAction.is(action);
   }

   /**
    * Whether `action` answers a popup request {@link trackPopupRequest} gave an
    * id, once that request is no longer {@link popupRequestId}. GLSP's hover
    * shows any answer, even one that comes after the pointer left, and the
    * popup then stays open. Remove it once GLSP's hover pairs the answer
    * itself. A test pins the hover, so a GLSP update that changes it fails.
    */
   protected isStalePopupAnswer(action: ResponseAction): boolean {
      return (
         SetPopupModelAction.is(action) &&
         action.responseId.startsWith(this.popupRequestPrefix) &&
         action.responseId !== this.popupRequestId
      );
   }

   /**
    * GLSP's hover sends a popup request without an id, and the server answers
    * with one of its own that the client cannot match. So the id is given here,
    * as `request` gives one, and the answer echoes it. A popup request replaces
    * the one before, even one a `request` caller sends. A popup the client
    * shows itself ends it, so a closed popup does: the close timer and Escape
    * dispatch an empty popup, and GLSP's close handler does once one shows. An
    * issue marker's popup ends it too, since it comes from the hover that would
    * have sent the next request. The {@link popupClosingKinds} end it too,
    * since they would have closed the popup had it shown, and its answer would
    * show where the node no longer is.
    */
   protected trackPopupRequest(action: Action): void {
      if (RequestPopupModelAction.is(action)) {
         action.requestId ||= this.popupRequestPrefix + RequestAction.generateRequestId();
         this.popupRequestId = action.requestId;
      } else if ((SetPopupModelAction.is(action) && !action.responseId) || this.popupClosingKinds.has(action.kind)) {
         // With a close delay longer than the open delay, the close of the node the pointer left ends
         // the next node's request and drops its answer. Telling the two apart needs a rebound hover
         // listener that tags its own close, a second seam to keep in step with GLSP's hover.
         this.popupRequestId = undefined;
      }
   }

   /**
    * Count `action` if it is an edit the client sends, even one whose dispatch
    * failed, since it may have reached the server. One dispatched again counts
    * again, except where a lifted block dispatches what it postponed.
    */
   protected trackEdit(action: Action): void {
      if (
         !this.trackEditsInFlight ||
         ServerAction.is(action) ||
         !this.isEdit(action) ||
         this.redispatched?.has(action) ||
         !this.hasHandler({ kind: RequestEditsSettledAction.KIND })
      ) {
         return;
      }
      const settle = ++this.settlesSent;
      const edit = new Deferred();
      this.editsBySettle.set(settle, edit);
      void this.pendingEdits.track(edit.promise);
      const answered = (): void => this.endEditsThrough(settle);
      this.request(RequestEditsSettledAction.create()).then(answered, answered);
   }

   /**
    * End every edit up to the `settle`th: the server handles a client's actions
    * in order, so its answer covers the edits before it, one whose own settle
    * was lost included.
    */
   protected endEditsThrough(settle: number): void {
      for (const [sent, edit] of this.editsBySettle) {
         if (sent > settle) {
            break;
         }
         edit.resolve();
         this.editsBySettle.delete(sent);
      }
   }

   protected logAction(action: Action): void {
      const { verb, direction } = this.classify(action);
      const summary = this.summarize(action);
      const timing = this.trackPair(action);
      const parts = [`${verb} action '${action.kind}'`, `[${direction}]`];
      if (summary) {
         parts.push(`[${summary}]`);
      }
      if (timing) {
         parts.push(timing);
      }
      this.hydraniumLogger.trace(parts.join(' '));
   }

   protected classify(action: Action): { verb: string; direction: string } {
      // GLSP marks client-produced computedBounds as ServerAction to suppress forwarding; stays local.
      if (LocalComputedBoundsAction.is(action)) {
         return { verb: 'Process', direction: 'client → client' };
      }
      // Marked by GLSPModelSource.messageReceived — arrived from the server.
      if (ServerAction.is(action)) {
         return { verb: 'Receive', direction: 'server → client' };
      }
      // GLSPModelSource is registered as handler for every server-handled kind (configureServeActions),
      // so its presence means the action will be forwarded to the server.
      const handlers = this.actionHandlerRegistry.get(action.kind);
      if (handlers.some(handler => handler instanceof GLSPModelSource)) {
         return { verb: 'Send', direction: 'client → server' };
      }
      return { verb: 'Process', direction: 'client → client' };
   }

   /** Emit `[request #N]` on the request side, `[response #N, Xms]` on the matching response side.
    *  Match first by requestId/responseId, then by known kind pairs (FIFO). */
   protected trackPair(action: Action): string {
      // Response side.
      if (ResponseAction.hasValidResponseId(action)) {
         const entry = this.pendingById.get(action.responseId);
         if (entry) {
            this.pendingById.delete(action.responseId);
            return this.formatResponse(entry);
         }
      }
      if (this.kindPairsReverse.has(action.kind)) {
         const queue = this.pendingByKind.get(action.kind);
         const entry = queue?.shift();
         if (entry) {
            return this.formatResponse(entry);
         }
      }
      // Request side.
      if (RequestAction.is(action) && action.requestId) {
         const entry = { id: ++this.pairCounter, sinceRequest: this.clock.stopwatch() };
         this.pendingById.set(action.requestId, entry);
         return `[request #${entry.id}]`;
      }
      if (this.kindPairs.has(action.kind)) {
         const responseKind = this.kindPairs.get(action.kind)!;
         const entry = { id: ++this.pairCounter, sinceRequest: this.clock.stopwatch() };
         const queue = this.pendingByKind.get(responseKind) ?? [];
         queue.push(entry);
         this.pendingByKind.set(responseKind, queue);
         return `[request #${entry.id}]`;
      }
      return '';
   }

   protected formatResponse(entry: { id: number; sinceRequest: Stopwatch }): string {
      const elapsed = Math.round(entry.sinceRequest.elapsedMs);
      return `[response #${entry.id}, ${elapsed}ms]`;
   }

   protected summarize(action: Action): string {
      if (SetModelAction.is(action) || UpdateModelAction.is(action)) {
         const root = action.newRoot;
         return `rootType=${root?.type ?? 'none'} children=${root?.children?.length ?? 0}`;
      }
      if (RequestBoundsAction.is(action)) {
         return `rootType=${action.newRoot.type} children=${action.newRoot.children?.length ?? 0}`;
      }
      if (ComputedBoundsAction.is(action)) {
         return `bounds=${action.bounds.length}`;
      }
      if (SetDirtyStateAction.is(action)) {
         return `isDirty=${action.isDirty} reason=${action.reason ?? 'n/a'}`;
      }
      if (SetEditModeAction.is(action)) {
         return `editMode=${action.editMode}`;
      }
      if (SetMarkersAction.is(action)) {
         return `markers=${action.markers.length}`;
      }
      if (StatusAction.is(action) || MessageAction.is(action)) {
         // severity=NONE with empty message is the conventional 'clear' signal.
         const clear = action.severity === 'NONE' && !action.message ? ' (clear)' : '';
         return `severity=${action.severity} message="${action.message.slice(0, 160)}"${clear}`;
      }
      if (RejectAction.is(action)) {
         return `reason="${action.message.slice(0, 160)}"`;
      }
      if (SetTypeHintsAction.is(action)) {
         return `shapes=${action.shapeHints.length} edges=${action.edgeHints.length}`;
      }
      if (RequestContextActions.is(action)) {
         return `contextId=${action.contextId}`;
      }
      if (RequestCheckEdgeAction.is(action) || CheckEdgeResultAction.is(action)) {
         return this.formatEdgeCheck(action);
      }
      return '';
   }

   /** An edge check and, on the result, its verdict, so the log shows why an edge tool refused a target. */
   protected formatEdgeCheck(action: RequestCheckEdgeAction | CheckEdgeResultAction): string {
      const verdict = CheckEdgeResultAction.is(action) ? ` isValid=${action.isValid}` : '';
      return `edgeType=${action.edgeType} source=${action.sourceElementId} target=${action.targetElementId ?? 'none'}${verdict}`;
   }
}
